'use client';

import { c as _c } from "react/compiler-runtime";
import { type PinnedFactiii } from '@shared/all/domains/factiiis';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import usePinUserFactiii from '@shared/utils/hooks/usePinUserFactiii';
import usePreference from '@shared/utils/hooks/usePreference';
import useUser from '@shared/utils/hooks/useUser';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, EllipsisVertical, Lock, Plus } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';
import AddFactiiiModal from '../spaces/AddFactiiiModal';
import Switch from '../UI/Switch';
type Props = {
  username: string;
  /** The profile's pins, so each row can say whether it is pinned. */
  pinnedFactiiis: PinnedFactiii[];
};

// The Factiiis section of a profile: the factiiis this user created. Pins are
// managed in the banner's PinnedFactiiisModal; a row here only toggles its own.
const UserFactiiisList = props => {
  const $ = _c(42);
  const {
    currentUser
  } = useUser();
  const pinUserFactiii = usePinUserFactiii(client);
  const {
    preferenceState,
    setPinnedFactiiisVisibility
  } = usePreference(client);
  const router = useRouter();
  const queryClient = useQueryClient();
  const [activeMenu, setActiveMenu] = useState(null);
  const [addFactiiiModalOpen, setAddFactiiiModalOpen] = useState(false);
  const [visibilityConfirmOpen, setVisibilityConfirmOpen] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const menuRefs = useRef(t0);
  const isOwner = currentUser.username === props.username;
  let t1;
  if ($[1] !== props.pinnedFactiiis) {
    t1 = new Set(props.pinnedFactiiis.map(_temp));
    $[1] = props.pinnedFactiiis;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const pinnedIds = t1;
  let t2;
  if ($[3] !== props.username) {
    t2 = ["factiiis", "byUser", props.username];
    $[3] = props.username;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const queryKey = t2;
  let t3;
  if ($[5] !== props.username) {
    t3 = t4 => {
      const {
        pageParam
      } = t4;
      return unwrapFromResult(client.factiiis.byUser.query({
        username: props.username,
        cursor: pageParam
      }));
    };
    $[5] = props.username;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== queryKey || $[8] !== t3) {
    t4 = {
      queryKey,
      queryFn: t3,
      initialPageParam: undefined as number | undefined,
      getNextPageParam: _temp2
    };
    $[7] = queryKey;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useInfiniteQuery(t4);
  const factiiis = data?.pages.flatMap(_temp3) ?? [];
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const handleClickOutside = event => {
        const target = event.target as Node;
        const isInside = Object.values(menuRefs.current).some(ref => ref ? ref.contains(target) : false);
        if (!isInside) {
          setActiveMenu(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useEffect(t5);
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = id => {
      setActiveMenu(prev => prev === id ? null : id);
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const toggleMenu = t6;
  const t7 = "flex min-h-[700px] flex-col p-2";
  const t8 = "space-y-2";
  let t9;
  if ($[12] !== isOwner) {
    t9 = isOwner && <div className="relative w-full"><div className="mx-auto flex items-center justify-between px-0 py-2 text-end lg:px-1"><p className="text-textSecondary/70">Make a new factiii</p><Button titleElement={<Plus />} classNames="w-auto" variant="SECONDARY" borderTransparent={false} onClick={event_0 => {
          event_0.preventDefault();
          setAddFactiiiModalOpen(true);
        }} /></div></div>;
    $[12] = isOwner;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== isOwner || $[15] !== preferenceState) {
    t10 = isOwner && <div className="mx-auto flex items-center justify-between px-0 py-2 text-end lg:px-1"><p className="text-textSecondary/70">{preferenceState.privatePinnedFactiiis ? "Private Pins" : "Public Pins"}</p><Switch state={preferenceState.privatePinnedFactiiis} onChange={() => setVisibilityConfirmOpen(true)} /></div>;
    $[14] = isOwner;
    $[15] = preferenceState;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = !isLoading && factiiis.length === 0 && <p className="p-4 text-base font-thin">{isOwner ? "You haven\u2019t made any factiiis yet." : `${props.username} hasn’t made any factiiis yet.`}</p>;
  const t12 = factiiis.map(f_0 => {
    const isPinned = pinnedIds.has(f_0.id);
    return <div className="mb-1 flex items-center justify-between rounded-md bg-background-secondary p-4 hover:bg-background-light lg:px-2" key={f_0.id}><Link className="mr-3 flex min-w-0 flex-1" href={`/fids/${f_0.id}`}><Avatar factiiiAvatarKey={f_0.avatarKey} factiiiId={f_0.id} size={50} /><div className="flex min-w-0 flex-col"><div className="ml-2 flex items-center gap-1.5 truncate">{f_0.name || "Unnamed Factiii"}{f_0.isPrivate && <Lock className="size-3.5 text-textSecondary" aria-label="Private" />}</div>{f_0.description && <div className="ml-2 line-clamp-2 text-sm text-textSecondary">{f_0.description}</div>}<div className="ml-2 text-sm text-textSecondary">{f_0.spaceSlug ? `s/${f_0.spaceSlug}/${f_0.slug}` : `factiiis/${f_0.id}`}</div></div></Link>{isOwner && currentUser.isAuthenticated && <div className="relative flex items-baseline"><button onClick={() => toggleMenu(f_0.id)} className="rounded-full p-1.5 text-textSecondary hover:bg-background-light hover:text-textPrimary" aria-label="More options"><EllipsisVertical className="size-5" /></button>{activeMenu === f_0.id && <div ref={el => {
          menuRefs.current[f_0.id] = el;
        }} className="absolute right-0 top-full z-10 mt-1 w-48 origin-top-right rounded-md bg-background-secondary shadow-lg ring-1 ring-ds-border-standard focus:outline-none" role="menu" aria-orientation="vertical"><div className="py-1" role="none"><button className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-background-light" role="menuitem" onClick={async () => {
              await pinUserFactiii(f_0);
              setActiveMenu(null);
            }}><Bookmark className="size-4" /><span>{isPinned ? "Unpin from profile" : "Pin to profile"}</span></button></div></div>}</div>}</div>;
  });
  let t13;
  if ($[17] !== fetchNextPage || $[18] !== hasNextPage || $[19] !== isFetchingNextPage) {
    t13 = hasNextPage && <Button text="View more" variant="SECONDARY" classNames="w-full" loading={isFetchingNextPage} onClick={() => void fetchNextPage()} />;
    $[17] = fetchNextPage;
    $[18] = hasNextPage;
    $[19] = isFetchingNextPage;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t12 || $[24] !== t13 || $[25] !== t9) {
    t14 = <div className={t7}><div className={t8}>{t9}{t10}{t11}{t12}{t13}</div></div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t9;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== preferenceState || $[28] !== setPinnedFactiiisVisibility || $[29] !== visibilityConfirmOpen) {
    t15 = visibilityConfirmOpen && <Confirmation title={preferenceState.privatePinnedFactiiis ? "Make your pins public?" : "Make your pins private?"} message={preferenceState.privatePinnedFactiiis ? "Anyone who visits your profile will see the factiiis you have pinned." : "Only you will see your pinned factiiis. They stay pinned, and you can make them public again at any time."} actionButtonText={preferenceState.privatePinnedFactiiis ? "Make public" : "Make private"} cancelHandler={() => setVisibilityConfirmOpen(false)} confirmHandler={async () => {
      await setPinnedFactiiisVisibility();
      setVisibilityConfirmOpen(false);
    }} />;
    $[27] = preferenceState;
    $[28] = setPinnedFactiiisVisibility;
    $[29] = visibilityConfirmOpen;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== addFactiiiModalOpen || $[32] !== isOwner || $[33] !== props.username || $[34] !== queryClient || $[35] !== queryKey || $[36] !== router) {
    t16 = isOwner && addFactiiiModalOpen && <AddFactiiiModal closeHandler={factiii => {
      setAddFactiiiModalOpen(false);
      if (factiii) {
        router.push(`/fids/${factiii.id}`);
      }
    }} type="USER" slug={props.username} refetch={() => void queryClient.invalidateQueries({
      queryKey
    })} />;
    $[31] = addFactiiiModalOpen;
    $[32] = isOwner;
    $[33] = props.username;
    $[34] = queryClient;
    $[35] = queryKey;
    $[36] = router;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t14 || $[39] !== t15 || $[40] !== t16) {
    t17 = <>{t14}{t15}{t16}</>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
};
export default UserFactiiisList;
function _temp(f) {
  return f.id;
}
function _temp2(lastPage) {
  return lastPage.nextCursor;
}
function _temp3(page) {
  return page.factiiis;
}